import dayjs from "dayjs";
import { createContext, type ReactNode, useContext, useState } from "react";

const zones = [
  {
    display: "US/Eastern",
    zone: "America/New_York",
  },
  {
    display: "US/Central",
    zone: "America/Chicago",
  },
  {
    display: "US/Mountain",
    zone: "America/Denver",
  },
  {
    display: "US/Pacific",
    zone: "America/Los_Angeles",
  },

  {
    display: "US/Alaska",
    zone: "America/Anchorage",
  },
  {
    display: "US/Hawaii",
    zone: "Pacific/Honolulu",
  },
  {
    display: "America/Phoenix",
    zone: "America/Phoenix",
  },
  {
    display: "America/Puerto_Rico",
    zone: "America/Puerto_Rico",
  },
];

interface TimezoneContextValue {
  tz: Timezone | null | undefined;
  setTz: (tz: string | Timezone) => void;
  userTz: Timezone | null;
}

const TimezoneContext = createContext({} as TimezoneContextValue);

export interface Timezone {
  display?: string;
  abbr: string;
  zone: string;
}

export function getZoneAbbr(zone: string): string {
  return dayjs().tz(zone).format("z");
}

export function guessTimezone(): Timezone {
  const zone = dayjs.tz.guess();
  const abbr = getZoneAbbr(zone);
  const display = zones.find((z) => z.zone === zone)?.display;
  return { abbr, zone, display };
}

export function getTimezone(zone: string): Timezone {
  return {
    zone,
    abbr: getZoneAbbr(zone),
  };
}

export function listUsTimezones(): Timezone[] {
  return zones.map(({ zone, display }) => ({
    zone,
    display,
    abbr: getZoneAbbr(zone),
  }));
}

function enrichTimezone(zone?: string | Timezone | null): Timezone | null {
  let enrichedZone: Timezone | null = null;
  if (typeof zone === "string") {
    enrichedZone = getTimezone(zone);
  }
  if (!zone) {
    enrichedZone = guessTimezone();
  }
  return enrichedZone;
}

export const usTimezones = listUsTimezones();

export const useTimezone = () => useContext(TimezoneContext);

export function TimezoneProvider({
  children,
  timezone,
}: {
  timezone?: string | Timezone | null;
  children: ReactNode;
}) {
  timezone = enrichTimezone(timezone);
  const [tz, setTz] = useState(timezone);

  const timezoneSetter = (zone?: string | Timezone | null) => {
    setTz(enrichTimezone(zone));
  };

  return (
    <TimezoneContext.Provider
      value={{ tz, setTz: timezoneSetter, userTz: guessTimezone() }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

export const selectableTimezones = [
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "America/Denver",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Phoenix",
  "America/Puerto_Rico",
];

export const zoneMapping = {
  "America/New_York": "US/Eastern",
  "America/Los_Angeles": "US/Pacific",
  "America/Chicago": "US/Central",
  "America/Denver": "US/Mountain",
  "America/Anchorage": "US/Alaska",
  "Pacific/Honolulu": "US/Hawaii",
  "America/Phoenix": "America/Phoenix",
  "America/Puerto_Rico": "America/Puerto_Rico",
};
